import { Inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { PendingService } from '@mydocma/app-container';
import { AuthService, ClaimTypes, JwtAuthService, User } from '@mydocma/auth';
import {
  SystemPagesService,
  SystemPageType,
} from '@mydocma/component-library/src/lib/system-pages';
import { ConfigProvider } from '@mydocma/core';
import { TranslateService } from '@ngx-translate/core';
import { catchError, filter, from, map, Observable, of, switchMap, take } from 'rxjs';
import { PR_CONFIG, PrConfig } from '../configuration/pr.config';
import { IShareLinkResponse, ShareAccessTypeEnum } from '../models/share-link';
import { ShareDocumentLinkService } from '../services/share-document-link.service';

@Injectable({
  providedIn: 'root',
})
export class ShareLinkGuard {
  private jwtAuthService: JwtAuthService;

  constructor(
    // @ts-ignore
    @Inject(PR_CONFIG) private config: PrConfig,
    private readonly authService: AuthService,
    private readonly shareDocumentLinkService: ShareDocumentLinkService,
    private readonly systemPagesService: SystemPagesService,
    private readonly pendingService: PendingService,
    private readonly translateService: TranslateService,
    private readonly configProvider: ConfigProvider<PrConfig>,
  ) {
    this.jwtAuthService = this.authService as any;
  }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    const customerId = route.paramMap.get('customerId');
    const shareId = route.paramMap.get('shareId');

    if (!shareId || !customerId) return of(false);

    return this.shareDocumentLinkService.getShareLinkInfo(customerId, shareId).pipe(
      switchMap((response: IShareLinkResponse) => {
        if (!response?.shareLink) {
          return of(false);
        }

        switch (response.shareAccessType) {
          case ShareAccessTypeEnum.PROTECTED:
            return this.handleProtectedAccess(response, state);

          case ShareAccessTypeEnum.PUBLIC:
            return of(true);

          default:
            this.handleUnauthorizedAccess(state);
            return of(false);
        }
      }),
      catchError((error) => {
        this.handleUnauthorizedAccess(state);
        return of(false);
      }),
    );
  }

  private handleProtectedAccess(
    response: IShareLinkResponse,
    state: RouterStateSnapshot,
  ): Observable<boolean> {
    return this.jwtAuthService.loadedState.pipe(
      filter((loaded: boolean) => loaded),
      take(1),
      switchMap(() => from(this.configProvider.loadConfigAsync())),
      switchMap((config: PrConfig) =>
        this.jwtAuthService.user.pipe(
          take(1),
          switchMap((user: User) => {
            if (!user) {
              this.jwtAuthService.startSignin(state.url);
              return of(false);
            }

            return from(
              this.jwtAuthService.refreshToken({
                [ClaimTypes.customer_id]: response?.customerId,
                [ClaimTypes.project_id]: response?.projectId,
                [ClaimTypes.app_Id]: config?.mydocmaConfig?.appId,
              }),
            ).pipe(
              map(() => {
                const claims = this.jwtAuthService.getAccessTokenClaims();
                if (!claims?.app_id || claims?.project !== response.projectId) {
                  this.handleUnauthorizedAccess(state);
                  return false;
                }
                if (!response?.docsCount) {
                  this.shareDocumentLinkService.handleNoDocumentFound(state);
                  return false;
                }
                return true;
              }),
              catchError((error) => {
                this.handleUnauthorizedAccess(state);
                return of(false);
              }),
            );
          }),
        ),
      ),
    );
  }

  private handleUnauthorizedAccess(state?: RouterStateSnapshot): void {
    const fullUrl = window.location.origin + state?.url;

    this.pendingService.closeInitLoader();
    this.systemPagesService.setPage({
      type: SystemPageType.NoAccess,
      urlOnRetry: state ? fullUrl : undefined,
      content: {
        title: this.translateService.instant('PR.systemPages.noAccess.title'),
        description:
          this.translateService.instant('PR.systemPages.noAccess.description') +
          '\n' +
          this.translateService.instant('PR.systemPages.noAccess.contactSupport'),
      },
      actions: [
        {
          label: this.translateService.instant('PR.buttons.logout'),
          clicked: () => {
            this.jwtAuthService.signout(null, fullUrl);
          },
        },
      ],
    });
    this.systemPagesService.openNoAccessPage();
  }
}
